import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip } from '../ui';
import { useBackgroundJobs } from '../../api/backgroundJobs';
import { jobTypeOf } from '../../config/jobTypes';
import { useDismissable } from '../../hooks';

const STATE_TONE = {
  WAITING: 'grey', RUNNING: 'blue', DONE: 'green', FAILED: 'red', CANCELLED: 'grey', UNKNOWN: 'amber',
};

/** "just now", "4m", "1h 12m" — how long a run has been queued. */
const elapsed = (iso) => {
  const t = Date.parse(iso ?? '');
  if (!Number.isFinite(t)) return '—';
  const min = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ${min % 60}m ago`;
};

/**
 * "⟳ N running" in the top bar, and the list behind it.
 *
 * Shown only while something is running: the top bar is crowded, and a
 * permanent "0 running" would be one more thing to read past. The list shows
 * the running work first, then what finished recently, so a toast that was
 * missed can still be found. Cancel is offered only while a job is WAITING —
 * a RUNNING one cannot be stopped.
 */
export default function JobsIndicator() {
  const { jobs, activeJobs, cancel } = useBackgroundJobs();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissable(open, ref, close);

  /* The elapsed times move while the list is open. */
  const [, tick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (!activeJobs.length) setOpen(false);
  }, [activeJobs.length]);

  if (!activeJobs.length) return null;

  const active = new Set(activeJobs);
  const rows = [...activeJobs, ...jobs.filter((j) => !active.has(j)).slice(0, 6)];

  return (
    <div className="jobs-ind" ref={ref}>
      <button
        className="icon-btn jobs-ind-btn"
        title="Analyses running in the background"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="jobs-spin" aria-hidden="true">⟳</span> {activeJobs.length} running
      </button>
      {open && (
        <div className="jobs-pop" role="dialog" aria-label="Background jobs">
          {rows.map((job) => {
            const type = jobTypeOf(job);
            const key = `${job.kind}:${job.key}`;
            return (
              <div key={key} className="jobs-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <b>{type.label}</b>
                  <div className="jobs-sub">
                    {job.subject ? (
                      <span
                        className="link"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setOpen(false); navigate(`/portfolios/${job.subject}`); }}
                      >
                        {job.subject}
                      </span>
                    ) : 'All portfolios'}
                    {' · '}{elapsed(job.enqueuedAt)}
                  </div>
                </div>
                <Chip tone={STATE_TONE[job.state] ?? 'grey'}>{job.state.toLowerCase()}</Chip>
                {job.state === 'WAITING' && !job.cancelled && (
                  <button
                    className="jobs-cancel"
                    disabled={cancelling === key}
                    onClick={async () => {
                      setCancelling(key);
                      try { await cancel(job); } finally { setCancelling(null); }
                    }}
                  >
                    {cancelling === key ? 'Cancelling…' : 'Cancel'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
